import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Building2, Users, ShieldCheck, BarChart3 } from 'lucide-react';
import { usePermissions } from '../../context/PermissionsContext';
import { PARTNER_PERMISSIONS } from '../../constants/permissions';
import { createUsageAnalyticsService } from '../../services/usageAnalyticsService';
import { n0, formatCost, getBrowserTz } from '../shared/UsageAnalytics/usageFormat';
import sharedStyles from '../PlatformAdmin/PlatformAdmin.module.scss';
import usageStyles from '../shared/UsageAnalytics/UsageAnalyticsPanel.module.scss';

const usageService = createUsageAnalyticsService('/partner-admin/usage');

/**
 * Partner landing page (Partner tier guide §5.1): company and user counts, the
 * features the partner may offer its companies, and this month's usage.
 */
const PartnerOverviewPanel = ({ me, ctx }) => {
  const { hasPartner } = usePermissions();
  const canUsage = hasPartner(PARTNER_PERMISSIONS.USAGE_ANALYTICS);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  useEffect(() => {
    if (!canUsage) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setUsageLoading(true);
        const data = await usageService.getSummary({ window: 'mtd', tz: getBrowserTz() });
        if (!cancelled) setUsage(data);
      } catch (err) {
        console.warn('Failed to load this month’s usage', err);
      } finally {
        if (!cancelled) setUsageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canUsage]);

  const partner = me?.partner || ctx.partner;
  // Recomputed each render so labels appear once the catalog arrives.
  const offer = (me?.company_permission_keys || []).map((key) => ctx.permissionEntry(key));

  if (!me) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  const totals = usage?.totals || {};
  const cost = formatCost(totals.estimated_cost, totals.cost_coverage_pct);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box className={usageStyles.kpiGrid}>
        <Box className={usageStyles.kpiTile}>
          <span className={usageStyles.kpiLabel}>
            <Building2 size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
            Companies
          </span>
          <span className={usageStyles.kpiValue}>{n0(partner?.company_count ?? 0)}</span>
        </Box>
        <Box className={usageStyles.kpiTile}>
          <span className={usageStyles.kpiLabel}>
            <Users size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
            Users
          </span>
          <span className={usageStyles.kpiValue}>{n0(partner?.user_count ?? 0)}</span>
        </Box>
        <Box className={usageStyles.kpiTile}>
          <span className={usageStyles.kpiLabel}>
            <ShieldCheck size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
            Features you can offer
          </span>
          <span className={usageStyles.kpiValue}>{n0(offer.length)}</span>
        </Box>
        {canUsage && (
          <Box className={usageStyles.kpiTile}>
            <span className={usageStyles.kpiLabel}>
              <BarChart3 size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
              This month's usage
            </span>
            {usageLoading && !usage ? (
              <CircularProgress size={18} color="primary" />
            ) : (
              <>
                <span className={usageStyles.kpiValue}>{n0(totals.total_tokens)} tokens</span>
                <span className={sharedStyles.muted}>
                  {n0(totals.calls)} calls · {cost.text}
                </span>
              </>
            )}
          </Box>
        )}
      </Box>

      <Box className={sharedStyles.tableCard} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Features you can offer your companies
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Set by the platform. You choose which of these each company gets under Feature limits.
        </Typography>
        {offer.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No features are included in your plan yet. Contact the platform.
          </Typography>
        ) : (
          <Box className={sharedStyles.roleChips}>
            {offer.map((entry) => (
              <span key={entry.key} className={sharedStyles.badge} title={entry.key}>
                {entry.label}
              </span>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PartnerOverviewPanel;
